import * as React from 'react';

interface TorchSvgProps {
  className?: string;
  /** When true, applies a CSS class for the subtle glow pulse (see TorchBanner.module.css). */
  pulse?: boolean;
  pulseClassName?: string;
}

/**
 * Compact inline SVG of the Torch of Peace. Static art (no canvas, no audio).
 * Designed to match the prototype's brass-handle-brass-flame proportions but
 * scaled to ~36×72 px native, scales freely via CSS.
 *
 * Pass `pulse` + `pulseClassName` to drive a CSS keyframe on the flame element.
 */
export function TorchSvg({
  className,
  pulse = false,
  pulseClassName,
}: TorchSvgProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 36 72"
      width="36"
      height="72"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="brassH" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#5a4018" />
          <stop offset="50%" stopColor="#f0d468" />
          <stop offset="100%" stopColor="#5a4018" />
        </linearGradient>
        <linearGradient id="handleH" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#2a1210" />
          <stop offset="50%" stopColor="#7a3e2e" />
          <stop offset="100%" stopColor="#2a1210" />
        </linearGradient>
        <radialGradient id="flameG" cx="0.5" cy="0.85" r="0.7">
          <stop offset="0%" stopColor="#e8dff0" stopOpacity="0.95" />
          <stop offset="35%" stopColor="#a799d8" stopOpacity="0.9" />
          <stop offset="70%" stopColor="#5e3b96" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#1c0d35" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Bottom brass cap */}
      <rect x="14" y="62" width="8" height="8" rx="1" fill="url(#brassH)" />

      {/* Mahogany handle */}
      <rect x="14" y="38" width="8" height="24" fill="url(#handleH)" />

      {/* Upper brass shaft (slight flare) */}
      <path
        d="M14 38 L13 28 L8 22 L8 18 L28 18 L28 22 L23 28 L22 38 Z"
        fill="url(#brassH)"
      />

      {/* Lip highlight */}
      <line x1="9" y1="18" x2="27" y2="18" stroke="#f0d870" strokeWidth="0.8" />

      {/* Flame (indigo/violet) */}
      <ellipse
        cx="18"
        cy="11"
        rx="9"
        ry="13"
        fill="url(#flameG)"
        className={pulse ? pulseClassName : undefined}
      />
    </svg>
  );
}
